// src/lib/localized-paths.ts
import { MenuId, SectionId, ItemId } from './enum';

// ============ Locale config ============
export const LOCALES = ['en', 'id'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'id';

// ============ Localized slugs ============
// Nilai enum = slug EN. Jadi untuk EN, mapping = identity.
// Untuk ID, map ke slug Indonesia.

export const menuSlugs: Record<Locale, Record<MenuId, string>> = {
  en: Object.fromEntries((Object.values(MenuId) as MenuId[]).map(v => [v, v])) as Record<MenuId, string>,
  id: {
    [MenuId.AboutUs]: 'tentang-kami',
    [MenuId.Services]: 'layanan',
    [MenuId.Publications]: 'publikasi',
    [MenuId.InvestorRelations]: 'hubungan-investor',
    [MenuId.Sustainability]: 'keberlanjutan',
    [MenuId.Faq]: MenuId.Faq,
    [MenuId.SiteMap]: 'peta-situs',
    [MenuId.ContactUs]: 'hubungi-kami',
    [MenuId.ProcurementInformation]: MenuId.ProcurementInformation,
  } as Record<MenuId, string>,
};

export const sectionSlugs: Record<Locale, Record<SectionId, string>> = {
  en: Object.fromEntries((Object.values(SectionId) as SectionId[]).map(v => [v, v])) as Record<SectionId, string>,
  id: {
    [SectionId.CompanyProfile]: 'profil-perseroan',
    [SectionId.Management]: 'manajemen-perseroan',
    [SectionId.Committees]: 'komite-komite',
    [SectionId.CorporateActions]: 'aksi-korporasi',
    [SectionId.Certifications]: 'sertifikasi-penghargaan',
    [SectionId.Governance]: 'tata-kelola',
    [SectionId.CorporateSecretary]: 'sekretaris-perusahaan',
    [SectionId.Affiliations]: 'hubungan-afiliasi',
    [SectionId.Securitization]: 'sekuritisasi',
    [SectionId.RetailEba]: 'eba-ritel',
    [SectionId.ShariaUnit]: 'unit-syariah',
    [SectionId.Financing]: 'pembiayaan',
    [SectionId.Bonds]: 'surat-utang',
    [SectionId.MortgageStandardization]: 'standarisasi-kpr',
    [SectionId.Training]: 'pelatihan',
    [SectionId.Ecosystem]: 'ekosistem-pembiayaan-perumahan',
    [SectionId.NewsActivities]: 'berita',
    [SectionId.Biweekly]: 'laporan-ekonomi-dan-pasar-dwimingguan',
    [SectionId.Emagazine]: 'emagazine',
    [SectionId.FinancialReports]: 'laporan-keuangan',
    [SectionId.AnnualReports]: 'laporan-tahunan',
    [SectionId.EbaSpPerformance]: 'kinerja-eba-sp',
    [SectionId.SocialBonds]: 'obligasi-sosial',
    [SectionId.InvestmentProducts]: 'produk-investasi',
    [SectionId.DividendInfo]: 'info-dividen',
    [SectionId.ImportantInfo]: 'informasi-penting',
    [SectionId.CorporateRatings]: 'peringkat-korporasi',
    [SectionId.CorporateSocialResponsibilities]: 'tentang-tanggungjawab-sosial-lingkungan',
    [SectionId.SustainabilityReport]: 'laporan-keberlanjutan',
    [SectionId.ProcurementInformation]: 'informasi-pengadaan',
    [SectionId.GeneralNotification]: 'pengumuman-umum',
  },
};

export const itemSlugs: Record<Locale, Record<ItemId, string>> = {
  en: Object.fromEntries((Object.values(ItemId) as ItemId[]).map(v => [v, v])) as Record<ItemId, string>,
  id: {
    [ItemId.Commissioners]: 'komisaris',
    [ItemId.Directors]: 'direksi',
    [ItemId.ShariaSupervisoryBoard]: 'dewan-pengawas-syariah',
    [ItemId.Policies]: 'kebijakan',
    [ItemId.TrainingSchedule]: 'pelatihan-jadwal',
    [ItemId.AssetInvestments]: 'investasi-aset',
    [ItemId.BondInvestments]: 'investasi-surat-utang',
    [ItemId.SocialBondInvestments]: 'investasi-obligasi-sosial',
    [ItemId.InvestorUpdate]: 'investor-update',
    [ItemId.PublicAnnouncement]: 'pengumuman-penawaran-umum',
    [ItemId.AnnualFinancialReport]: 'laporan-tahunan-keuangan',
    [ItemId.InterimFinancialReport]: 'laporan-keuangan-interim',
    [ItemId.ListOfPlannedProcurement]: 'daftar-perencanaan',
    [ItemId.TenderInformation]: 'informasi-tender',
  },
};

// ============ Helpers umum ============
export function hasLocalePrefix(pathname: string): boolean {
  return LOCALES.some(l => pathname === `/${l}` || pathname.startsWith(`/${l}/`));
}

function joinPath(...segments: Array<string | undefined>) {
  const cleaned = segments.filter(Boolean).map(s => s!.replace(/^\/+|\/+$/g, ''));
  return '/' + cleaned.join('/');
}

// Type guards runtime
const MENU_VALUES = new Set(Object.values(MenuId) as string[]);
const SECTION_VALUES = new Set(Object.values(SectionId) as string[]);
const ITEM_VALUES = new Set(Object.values(ItemId) as string[]);

function isMenu(x: unknown): x is MenuId { return typeof x === 'string' && MENU_VALUES.has(x); }
function isSection(x: unknown): x is SectionId { return typeof x === 'string' && SECTION_VALUES.has(x); }
function isItem(x: unknown): x is ItemId { return typeof x === 'string' && ITEM_VALUES.has(x); }

/** Ambil slug terlokalisasi untuk satu segmen (menu/section/item) */
export function slugFor(locale: Locale, seg: MenuId | SectionId | ItemId | string) {
  if (isMenu(seg)) return menuSlugs[locale][seg];
  if (isSection(seg)) return sectionSlugs[locale][seg];
  if (isItem(seg)) return itemSlugs[locale][seg];
  return String(seg); // literal segment
}

/** Build href terlokalisasi dari urutan segmen */
export function href(locale: Locale, ...segments: Array<MenuId | SectionId | ItemId | string>) {
  const localizedSegments = segments.map(seg => slugFor(locale, seg));
  return joinPath(locale, ...localizedSegments);
}